import { useState, FormEvent, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authApi } from '../services/api';
import { UserLogin } from '@dsgvo-buddy/types';

export default function LandingPage() {
  const navigate = useNavigate();
  const solutionRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const loginRef = useRef<HTMLDivElement>(null);

  const [loginData, setLoginData] = useState<UserLogin>({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const response = await authApi.login(loginData);
      if (response.success && response.data) {
        navigate('/dashboard');
      } else {
        setLoginError(response.error || 'Login fehlgeschlagen');
      }
    } catch {
      setLoginError('Ein Fehler ist aufgetreten');
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: "'72', '72full', Arial, Helvetica, sans-serif", color: '#1a1d21' }}>

      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: 'rgba(255,255,255,0.97)',
        borderBottom: '1px solid #e5e5e5',
        backdropFilter: 'blur(8px)',
      }}>
        <div style={{
          maxWidth: '1140px',
          margin: '0 auto',
          padding: '0 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '3.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <img src="/logo.png" alt="DSGVOeasy" style={{ height: '2rem', width: 'auto' }} />
            <span style={{ fontWeight: 700, fontSize: '1.125rem', color: '#1a1d21' }}>DSGVOeasy</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
            <div className="landing-nav-links" style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
              <button onClick={() => scrollTo(solutionRef)} style={navLinkStyle}>Die Lösung</button>
              <button onClick={() => scrollTo(featuresRef)} style={navLinkStyle}>Funktionen</button>
              <button onClick={() => scrollTo(pricingRef)} style={navLinkStyle}>Preise</button>
              <button onClick={() => scrollTo(loginRef)} style={navLinkStyle}>Login</button>
            </div>
            <Link to="/register" className="landing-nav-cta" style={{
              background: 'linear-gradient(135deg, #0070f2, #0054b6)',
              color: 'white',
              padding: '0.5rem 1.25rem',
              borderRadius: '0.5rem',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              transition: 'opacity 0.2s',
            }}>Kostenlos testen</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero" style={{
        paddingTop: '6.5rem',
        paddingBottom: '5rem',
        background: 'linear-gradient(160deg, #f0f5ff 0%, #e8f0fe 40%, #ffffff 100%)',
      }}>
        <div style={{ maxWidth: '1140px', margin: '0 auto', padding: '0 2rem', textAlign: 'center' }}>
          <div style={{
            display: 'inline-block',
            background: '#e8f0fe',
            color: '#0054b6',
            padding: '0.375rem 1rem',
            borderRadius: '2rem',
            fontSize: '0.8125rem',
            fontWeight: 600,
            marginBottom: '1.5rem',
          }}>
            DSGVO-Compliance leicht gemacht
          </div>

          <h1 style={{
            fontSize: 'clamp(2rem, 4vw, 3.25rem)',
            fontWeight: 700,
            lineHeight: 1.2,
            color: '#1a1d21',
            maxWidth: '820px',
            margin: '0 auto 1.5rem',
          }}>
            DSGVO einfach abbilden.<br />Schon ab 7,99 € im Monat.
          </h1>

          <p style={{
            fontSize: '1.1875rem',
            color: '#556170',
            maxWidth: '660px',
            margin: '0 auto 2.5rem',
            lineHeight: 1.7,
          }}>
            Erstelle Verarbeitungsverzeichnisse, TOMs und DSGVO-Dokumentation
            Schritt für Schritt — einfach verständlich, jederzeit monatlich kündbar und ohne lange Vertragsbindung.
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '4rem' }}>
            <Link to="/register" style={{
              background: 'linear-gradient(135deg, #0070f2, #0054b6)',
              color: 'white',
              padding: '0.875rem 2rem',
              borderRadius: '0.625rem',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              boxShadow: '0 4px 14px rgba(0,112,242,0.3)',
            }}>Kostenlos starten</Link>
            <button onClick={() => scrollTo(featuresRef)} style={{
              background: 'white',
              color: '#0070f2',
              padding: '0.875rem 2rem',
              borderRadius: '0.625rem',
              fontWeight: 600,
              fontSize: '1rem',
              border: '1.5px solid #0070f2',
              cursor: 'pointer',
            }}>Funktionen entdecken</button>
          </div>

          {/* Produktvideo */}
          <div style={{
            maxWidth: '900px',
            margin: '0 auto',
            borderRadius: '0.875rem',
            overflow: 'hidden',
            boxShadow: '0 24px 60px rgba(0,0,0,0.16)',
            background: '#e8f0fe',
          }}>
            <video
              src="/video/dsgvoeasy-launch.mp4"
              poster="/video/dsgvoeasy-launch.jpg"
              controls
              playsInline
              preload="none"
              aria-label="DSGVOeasy Produktvideo"
              style={{
                width: '100%',
                aspectRatio: '16 / 9',
                display: 'block',
              }}
            />
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section style={{
        padding: '2.5rem 2rem',
        borderBottom: '1px solid #eee',
        background: 'white',
      }}>
        <div style={{ maxWidth: '1140px', margin: '0 auto', textAlign: 'center' }}>
          <p style={{
            color: '#8c9196',
            fontSize: '0.8125rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '1rem',
          }}>
            Erfüllt die Anforderungen von
          </p>
          <div className="landing-trust-bar" style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '3rem',
            flexWrap: 'wrap',
            color: '#6a7178',
            fontSize: '0.9375rem',
            fontWeight: 600,
          }}>
            <span>Art. 30 DSGVO</span>
            <span>Art. 28 DSGVO</span>
            <span>Art. 32 DSGVO</span>
            <span>Art. 13/14 DSGVO</span>
            <span>Art. 15-22 DSGVO</span>
            <span>Art. 33/34 DSGVO</span>
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section ref={solutionRef} className="landing-section" style={{ padding: '5.5rem 2rem', background: 'white' }}>
        <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.25rem)', fontWeight: 700, marginBottom: '1rem', color: '#1a1d21' }}>
              Alle Datenschutz-Prozesse in einer Plattform
            </h2>
            <p style={{ fontSize: '1.0625rem', color: '#556170', maxWidth: '600px', margin: '0 auto', lineHeight: 1.6 }}>
              Statt unübersichtlicher Excel-Listen und verstreuter Dokumente
              managen Sie alles zentral, strukturiert und nachvollziehbar.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5rem' }}>
            {showcaseItems.map((item, i) => {
              const isEven = i % 2 === 0;
              return (
                <div
                  key={i}
                  className="landing-showcase-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '4rem',
                    alignItems: 'center',
                  }}
                >
                  {/* Screenshot side */}
                  <div style={{ order: isEven ? 0 : 1 }}>
                    <img
                      src={`/screenshots/${item.screenshot}`}
                      alt={item.title}
                      style={{
                        borderRadius: '0.75rem',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                        width: '100%',
                        maxWidth: '560px',
                        display: 'block',
                        transform: isEven
                          ? 'perspective(1200px) rotateY(-2deg) rotateX(1deg)'
                          : 'perspective(1200px) rotateY(2deg) rotateX(1deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </div>

                  {/* Text side */}
                  <div style={{ order: isEven ? 1 : 0 }}>
                    <div style={{
                      width: '3rem',
                      height: '3rem',
                      borderRadius: '0.875rem',
                      background: item.iconBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.375rem',
                      marginBottom: '1.25rem',
                    }}>
                      {item.icon}
                    </div>
                    <h3 style={{
                      fontSize: '1.375rem',
                      fontWeight: 700,
                      marginBottom: '0.875rem',
                      color: '#1a1d21',
                      lineHeight: 1.3,
                    }}>
                      {item.title}
                    </h3>
                    <p style={{
                      fontSize: '1rem',
                      color: '#556170',
                      lineHeight: 1.7,
                      marginBottom: '1.25rem',
                    }}>
                      {item.description}
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {item.bullets.map((bullet, bi) => (
                        <li key={bi} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem', fontSize: '0.9375rem', color: '#32363a' }}>
                          <span style={{
                            flexShrink: 0,
                            marginTop: '0.1rem',
                            width: '1.125rem',
                            height: '1.125rem',
                            borderRadius: '50%',
                            background: '#e8f0fe',
                            color: '#0070f2',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.625rem',
                            fontWeight: 800,
                          }}>✓</span>
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Template/Wizard Highlight */}
      <section ref={featuresRef} style={{ padding: '5.5rem 2rem', background: '#f6f8fb' }}>
        <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.25rem)', fontWeight: 700, marginBottom: '1rem', color: '#1a1d21' }}>
              Über 50 Branchenvorlagen – sofort einsatzbereit
            </h2>
            <p style={{ fontSize: '1.0625rem', color: '#556170', maxWidth: '620px', margin: '0 auto', lineHeight: 1.6 }}>
              Kein leeres Formular, kein Raten. Jede Branche bringt vorkonfigurierte
              Vorlagen mit passenden Verarbeitungstätigkeiten, Rechtsgrundlagen und Fristen.
            </p>
          </div>

          <div className="landing-template-grid" style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem',
            justifyContent: 'center',
            marginBottom: '3rem',
          }}>
            {templateCategories.map((cat, i) => (
              <div key={i} style={{
                background: 'white',
                border: '1.5px solid #dce3ee',
                borderRadius: '2rem',
                padding: '0.5rem 1.125rem',
                fontSize: '0.9375rem',
                fontWeight: 600,
                color: '#1a1d21',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}>
                <span style={{ fontSize: '1.0625rem' }}>{cat.icon}</span>
                {cat.label}
              </div>
            ))}
          </div>

          <div style={{
            background: 'white',
            borderRadius: '1rem',
            padding: '2rem 2.5rem',
            border: '1px solid #dce3ee',
            textAlign: 'center',
            maxWidth: '780px',
            margin: '0 auto',
            boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
          }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>🎉</div>
            <p style={{ fontSize: '1rem', color: '#556170', lineHeight: 1.7, margin: 0 }}>
              Jeder Wizard führt Sie <strong style={{ color: '#1a1d21' }}>Schritt für Schritt</strong> durch
              die Eingabe -- mit Hilfetexten, Empfehlungen und automatischer Validierung.
              Keine juristischen Vorkenntnisse erforderlich.
            </p>
          </div>
        </div>
      </section>

      {/* Warum DSGVOeasy */}
      <section className="landing-section" style={{ padding: '5.5rem 2rem', background: 'white' }}>
        <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.25rem)', fontWeight: 700, marginBottom: '1rem', color: '#1a1d21' }}>
              Warum DSGVOeasy?
            </h2>
            <p style={{ fontSize: '1.0625rem', color: '#556170', maxWidth: '560px', margin: '0 auto', lineHeight: 1.6 }}>
              Entwickelt von Datenschutz-Praktikern -- für Unternehmen,
              die Compliance ernst nehmen, ohne darin zu ertrinken.
            </p>
          </div>

          <div className="landing-benefits-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '2rem',
          }}>
            {benefits.map((b, i) => (
              <div key={i} style={{
                textAlign: 'center',
                padding: '2rem 1.5rem',
                borderRadius: '1rem',
                background: '#f6f8fb',
                border: '1px solid #edf0f4',
              }}>
                <div style={{
                  fontSize: '2.75rem',
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #0070f2, #0054b6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '0.75rem',
                  lineHeight: 1,
                }}>{b.stat}</div>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1a1d21' }}>
                  {b.title}
                </h3>
                <p style={{ fontSize: '0.875rem', color: '#6a7178', lineHeight: 1.6, margin: 0 }}>
                  {b.text}
                </p>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <Link to="/register" style={{
              background: 'linear-gradient(135deg, #0070f2, #0054b6)',
              color: 'white',
              padding: '0.875rem 2.5rem',
              borderRadius: '0.625rem',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              boxShadow: '0 4px 14px rgba(0,112,242,0.3)',
            }}>Jetzt starten -- kostenlos</Link>
          </div>
        </div>
      </section>

      {/* Preise */}
      <section ref={pricingRef} className="landing-section" style={{ padding: '5.5rem 2rem', background: '#f6f8fb' }}>
        <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
            <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.25rem)', fontWeight: 700, marginBottom: '1rem', color: '#1a1d21' }}>
              Einfache, faire Preise
            </h2>
            <p style={{ fontSize: '1.0625rem', color: '#556170', maxWidth: '520px', margin: '0 auto', lineHeight: 1.6 }}>
              Keine versteckten Kosten. Starten Sie kostenlos und wachsen Sie mit Ihren Anforderungen.
            </p>
          </div>

          <div className="landing-pricing-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
            maxWidth: '960px',
            margin: '0 auto',
          }}>
            {/* Free */}
            <div style={{
              borderRadius: '1rem',
              border: '1px solid #edf0f4',
              padding: '2.5rem 2rem',
              background: 'white',
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1a1d21' }}>Starter</h3>
              <p style={{ color: '#6a7178', fontSize: '0.875rem', marginBottom: '1.5rem' }}>Perfekt zum Einstieg</p>
              <div style={{ marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '2.5rem', fontWeight: 800, color: '#1a1d21' }}>Kostenlos</span>
              </div>
              <Link to="/register" style={{
                display: 'block',
                textAlign: 'center',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1.5px solid #0070f2',
                color: '#0070f2',
                textDecoration: 'none',
                fontWeight: 600,
                marginBottom: '2rem',
              }}>Kostenlos starten</Link>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {starterFeatures.map((f, i) => (
                  <li key={i} style={{ padding: '0.5rem 0', fontSize: '0.9375rem', color: '#32363a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#36a41d', fontWeight: 700 }}>✓</span> {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* Professional */}
            <div style={{
              borderRadius: '1rem',
              border: '2px solid #0070f2',
              padding: '2.5rem 2rem',
              background: 'white',
              position: 'relative',
              boxShadow: '0 8px 30px rgba(0,112,242,0.12)',
            }}>
              <div style={{
                position: 'absolute',
                top: '-0.75rem',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'linear-gradient(135deg, #0070f2, #0054b6)',
                color: 'white',
                padding: '0.25rem 1rem',
                borderRadius: '1rem',
                fontSize: '0.75rem',
                fontWeight: 700,
              }}>Beliebt</div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1a1d21' }}>Professional</h3>
              <p style={{ color: '#6a7178', fontSize: '0.875rem', marginBottom: '1.5rem' }}>Für wachsende Unternehmen</p>
              <div style={{ marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '2.5rem', fontWeight: 800, color: '#1a1d21' }}>7,99 EUR</span>
                <span style={{ color: '#8c9196', fontSize: '0.875rem' }}> pro Monat (netto)</span>
                <div style={{ color: '#6a7178', fontSize: '0.875rem', marginTop: '0.375rem' }}>
                  Günstiger Preis für umfassende Lösung
                </div>
              </div>
              <Link to="/register" style={{
                display: 'block',
                textAlign: 'center',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                background: 'linear-gradient(135deg, #0070f2, #0054b6)',
                color: 'white',
                textDecoration: 'none',
                fontWeight: 600,
                marginBottom: '2rem',
              }}>14 Tage kostenlos testen</Link>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {proFeatures.map((f, i) => (
                  <li key={i} style={{ padding: '0.5rem 0', fontSize: '0.9375rem', color: '#32363a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#36a41d', fontWeight: 700 }}>✓</span> {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* Enterprise */}
            <div style={{
              borderRadius: '1rem',
              border: '1px solid #edf0f4',
              padding: '2.5rem 2rem',
              background: 'white',
            }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1a1d21' }}>Enterprise</h3>
              <p style={{ color: '#6a7178', fontSize: '0.875rem', marginBottom: '1.5rem' }}>Für Konzerne & Gruppen</p>
              <div style={{ marginBottom: '1.5rem' }}>
                <span style={{ fontSize: '2.5rem', fontWeight: 800, color: '#1a1d21' }}>Individuell</span>
              </div>
              <Link to="/register" style={{
                display: 'block',
                textAlign: 'center',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                border: '1.5px solid #0070f2',
                color: '#0070f2',
                textDecoration: 'none',
                fontWeight: 600,
                marginBottom: '2rem',
              }}>Kontakt aufnehmen</Link>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {enterpriseFeatures.map((f, i) => (
                  <li key={i} style={{ padding: '0.5rem 0', fontSize: '0.9375rem', color: '#32363a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ color: '#36a41d', fontWeight: 700 }}>✓</span> {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Login */}
      <section ref={loginRef} style={{ padding: '5rem 2rem', background: 'white' }}>
        <div style={{ maxWidth: '420px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1a1d21' }}>
              Anmelden
            </h2>
            <p style={{ color: '#6a7178', fontSize: '0.9375rem' }}>
              Melden Sie sich in Ihrem DSGVOeasy Konto an
            </p>
          </div>

          <div style={{
            background: 'white',
            borderRadius: '1rem',
            padding: '2rem',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid #edf0f4',
          }}>
            <form onSubmit={handleLogin}>
              {loginError && (
                <div style={{
                  background: '#fef2f2',
                  color: '#bb0000',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  marginBottom: '1rem',
                }}>{loginError}</div>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: 600, fontSize: '0.875rem', color: '#32363a' }}>
                  E-Mail-Adresse
                </label>
                <input
                  type="email"
                  value={loginData.email}
                  onChange={e => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  disabled={loginLoading}
                  placeholder="ihre.email@firma.de"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.375rem', fontWeight: 600, fontSize: '0.875rem', color: '#32363a' }}>
                  Passwort
                </label>
                <input
                  type="password"
                  value={loginData.password}
                  onChange={e => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  disabled={loginLoading}
                  placeholder="Passwort eingeben"
                  style={inputStyle}
                />
              </div>

              <button
                type="submit"
                disabled={loginLoading}
                style={{
                  width: '100%',
                  padding: '0.8rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  background: 'linear-gradient(135deg, #0070f2, #0054b6)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.9375rem',
                  cursor: loginLoading ? 'not-allowed' : 'pointer',
                  opacity: loginLoading ? 0.7 : 1,
                }}
              >
                {loginLoading ? 'Anmeldung läuft...' : 'Anmelden'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: '1.25rem' }}>
              <span style={{ color: '#8c9196', fontSize: '0.8125rem' }}>
                Noch kein Konto?{' '}
                <Link to="/register" style={{ color: '#0070f2', fontWeight: 600, textDecoration: 'none' }}>
                  Kostenlos registrieren
                </Link>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '2.5rem 2rem',
        background: '#1a1d21',
        color: '#8c9196',
        textAlign: 'center',
        fontSize: '0.875rem',
      }}>
        <div style={{ maxWidth: '1140px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <img src="/logo.png" alt="DSGVOeasy" style={{ height: '1.5rem', width: 'auto' }} />
            <span style={{ color: '#ccc', fontWeight: 600 }}>DSGVOeasy</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginBottom: '0.75rem' }}>
            <Link to="/datenschutz" style={{ color: '#8c9196', textDecoration: 'none', fontSize: '0.8125rem' }}>Datenschutz</Link>
            <Link to="/impressum" style={{ color: '#8c9196', textDecoration: 'none', fontSize: '0.8125rem' }}>Impressum</Link>
            <Link to="/agb" style={{ color: '#8c9196', textDecoration: 'none', fontSize: '0.8125rem' }}>AGB</Link>
            <Link to="/widerruf" style={{ color: '#8c9196', textDecoration: 'none', fontSize: '0.8125rem' }}>Widerruf</Link>
          </div>
          <p>&copy; {new Date().getFullYear()} DSGVOeasy. Alle Rechte vorbehalten.</p>
        </div>
      </footer>
    </div>
  );
}

// --- Styles ---

const navLinkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#556170',
  fontWeight: 600,
  fontSize: '0.9375rem',
  cursor: 'pointer',
  padding: 0,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.7rem 0.875rem',
  borderRadius: '0.5rem',
  border: '1.5px solid #d1d5db',
  fontSize: '0.9375rem',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

// --- Data ---

const showcaseItems = [
  {
    screenshot: 'dashboard.png',
    icon: '📊',
    iconBg: '#e8f0fe',
    title: 'Alles auf einen Blick',
    description: 'Das Dashboard gibt Ihnen sofort einen vollständigen Überblick über Ihren Compliance-Status. Offene Aufgaben, aktuelle Statistiken und Schnellzugriffe auf alle Bereiche -- damit Sie stets wissen, wo Handlungsbedarf besteht.',
    bullets: [
      'Compliance-Status auf einen Blick',
      'Offene Aufgaben und Fristen im Fokus',
      'Schnellzugriff auf alle Module',
      'Statistiken und Fortschrittsanzeigen',
    ],
  },
  {
    screenshot: 'vvt-wizard.png',
    icon: '📝',
    iconBg: '#fef3e0',
    title: 'Verarbeitungen in Minuten statt Stunden',
    description: 'Der geführte VVT-Wizard nimmt Ihnen die Arbeit ab: Über 50 Branchenvorlagen für Personalwesen, Marketing, IT, Gesundheit und mehr sind vorausgefüllt mit passenden Rechtsgrundlagen, empfohlenen Löschfristen und typischen Datenkategorien.',
    bullets: [
      '50+ Vorlagen für alle Branchen',
      'Vorausgefüllte Rechtsgrundlagen (Art. 6 DSGVO)',
      'Automatische Empfehlung von Löschfristen',
      'Schritt-für-Schritt-Führung mit Hilfetexten',
    ],
  },
  {
    screenshot: 'dienstleister.png',
    icon: '🤝',
    iconBg: '#e8f5e9',
    title: 'Dienstleister und AVV-Verträge zentral verwalten',
    description: 'Behalten Sie den Überblick über alle Auftragsverarbeiter. Ein Katalog mit über 40 vorbereiteten Dienstleister-Profilen (AWS, Google, Microsoft u.v.m.) vereinfacht die Erfassung, und der AVV-Status ist jederzeit auf einen Blick ersichtlich.',
    bullets: [
      '40+ vorkonfigurierte Dienstleister-Profile',
      'AVV-Vertragsstatus und -fristen im Blick',
      'Unterauftragnehmer-Verwaltung',
      'Compliance-Status pro Anbieter',
    ],
  },
  {
    screenshot: 'tom.png',
    icon: '🔒',
    iconBg: '#f3e8fd',
    title: 'Technische und organisatorische Massnahmen dokumentieren',
    description: 'Erfüllen Sie Art. 32 DSGVO lückenlos: Dokumentieren Sie TOM-Maßnahmen kategorisiert nach Zugriffskontrolle, Verschlüsselung, Pseudonymisierung u.v.m. -- mit Bewertung der Wirksamkeit, Wiedervorlageterminen und direkter Verknüpfung zu Verarbeitungen.',
    bullets: [
      'Strukturierte Kategorien (Zugriff, Verschlüsselung, ...)',
      'Wirksamkeitsbewertung und Review-Planung',
      'Verknüpfung mit Verarbeitungstätigkeiten',
      'PDF-Export für Audits und Aufsichtsbehörden',
    ],
  },
  {
    screenshot: 'datenschutzhinweise.png',
    icon: '📄',
    iconBg: '#fff3e0',
    title: 'Datenschutzhinweise automatisch generieren',
    description: 'DSGVOeasy generiert Datenschutzhinweise direkt aus Ihren erfassten Verarbeitungstätigkeiten -- vollständig nach Art. 13/14 DSGVO, mit Versionierung und für mehrere Kanäle (Website, App, Bewerbungsformular, Videoüberwachung ...).',
    bullets: [
      'Automatische Generierung aus dem VVT',
      'Versionierung und Änderungshistorie',
      'Multi-Kanal: Website, App, Formulare',
      'Immer auf dem aktuellen Stand',
    ],
  },
  {
    screenshot: 'vorfaelle.png',
    icon: '🚨',
    iconBg: '#ffebee',
    title: 'Datenschutzvorfaelle und Betroffenenrechte im Griff',
    description: 'Im Ernstfall zählt jede Stunde: DSGVOeasy überwacht die 72-Stunden-Meldefrist nach Art. 33 DSGVO, führt Sie durch die strukturierte Vorfall-Dokumentation und verwaltet gleichzeitig alle Auskunfts-, Löschungs- und Widerspruchsanfragen mit Fristüberwachung.',
    bullets: [
      '72-Stunden-Fristüberwachung (Art. 33 DSGVO)',
      'Strukturierte Vorfall-Dokumentation',
      'Betroffenenrechte mit Deadline-Monitoring',
      'Audit-sicheres Protokoll aller Vorgänge',
    ],
  },
];

const templateCategories = [
  { icon: '👨‍💻', label: 'Personalwesen' },
  { icon: '📊', label: 'Marketing & Vertrieb' },
  { icon: '💻', label: 'IT & Entwicklung' },
  { icon: '🏥', label: 'Gesundheitswesen' },
  { icon: '🛒', label: 'E-Commerce' },
  { icon: '💵', label: 'Finanzen & Buchhaltung' },
  { icon: '🏫', label: 'Bildung' },
  { icon: '🏠', label: 'Immobilien' },
  { icon: '🍳', label: 'Gastronomie' },
  { icon: '🚚', label: 'Logistik' },
  { icon: '🔨', label: 'Handwerk' },
  { icon: '⚖️', label: 'Rechtsanwälte' },
];

const benefits = [
  { stat: '80%', title: 'Weniger Aufwand', text: 'Automatisierte Prozesse und Vorlagen ersetzen manuelle Excel-Pflege und E-Mail-Ketten dauerhaft.' },
  { stat: '100%', title: 'Audit-ready', text: 'Vollständiges Audit-Protokoll und PDF-Exporte für jede Aufsichtsbehörden-Prüfung -- auf Knopfdruck.' },
  { stat: '5 Min', title: 'Bis zum ersten VVT', text: 'Geführter Wizard mit Branchenvorlagen statt leerer Formulare -- sofort einsatzbereit.' },
  { stat: '24/7', title: 'Immer verfügbar', text: 'Cloudbasiert, sicher gehostet in Deutschland. Kein Setup, keine Installation nötig.' },
];

const starterFeatures = [
  'Alle Funktionen verfügbar',
  'Max. 10 Einträge pro Bereich',
  '1 Benutzer',
  'Verarbeitungsverzeichnis',
  'Dienstleister & AVV',
  'TOM-Dokumentation',
  'Datenschutzhinweis-Generator',
  'Betroffenenrechte & Vorfälle',
  'PDF-Export',
];

const proFeatures = [
  'Alles aus Starter, plus:',
  'Unbegrenzte Einträge',
  'Bis zu 25 Benutzer',
  'Freigabe-Workflows',
  'Audit-Protokoll',
  'JSON-Import',
  'Günstiger Preis für umfassende Lösung',
];

const enterpriseFeatures = [
  'Alles aus Professional, plus:',
  'Unbegrenzte Benutzer',
  'Multi-Organisations-Support',
  'SSO / SAML-Integration',
  'Dedizierter Ansprechpartner',
  'Individuelle Anpassungen',
  'On-Premise-Option',
];
